/**
 * The role badge — ONE shared helper (promoted out of `MembersList` in
 * DNKADM-0304 U9, the `serviceLabels.ts`/`compareStaffLabels` precedent) so
 * the Members roster and the Settings → General role card render the SAME
 * fact with the SAME treatment, including the fallback for an unrecognised
 * role value (raw label, paused-violet pair — never a crash, never plain
 * text).
 *
 * Colors are mode-aware (DNKADM-0269): reuses the shared semantic maps where
 * a role's pair matches exactly (OWNER = active green, ADMIN = performance-
 * plus teal, USER/fallback = paused violet, GUEST = the account-badge
 * unclaimed amber); SUPER_ADMIN is a distinct purple and STAFF (DNKADM-0360)
 * a distinct blue, neither with a shared-map match, so both live in
 * `theme/maps/members` — STAFF using the same pair as the platform-members
 * roster so one role badges identically on both screens.
 */
import { buildSemanticMaps } from '@/theme';
import { buildAccountBadgePairs } from '@/theme/maps/accountBadges';
import { buildMembersMaps } from '@/theme/maps/members';
import type { UserRole } from '@/api/users';
import type { BadgeStyle } from './accountState';

/** Role badge colors, keyed by role (DNKAPI roster roles). */
function roleBadges(mode: 'light' | 'dark'): Record<string, BadgeStyle> {
  const maps = buildSemanticMaps(mode);
  const members = buildMembersMaps(mode);
  const accountBadges = buildAccountBadgePairs(mode);
  return {
    OWNER: { ...maps.status.active, label: 'Owner', bg: maps.greenTints.chip },
    SUPER_ADMIN: {
      ...members.roleBadges.superAdmin,
      label: 'Super admin',
      hint: 'Elevated administrator',
    },
    ADMIN: { ...maps.tierChip.performance_plus, label: 'Manager' },
    STAFF: { ...members.roleBadges.staff, label: 'Front desk' },
    USER: { ...maps.status.paused, label: 'User' },
    GUEST: { ...accountBadges.unclaimed, label: 'Guest' },
  };
}

/**
 * The badge for a role — an unrecognised value degrades to its raw label.
 *
 * DNKADM-0491 role words: OWNER "Owner", ADMIN "Manager", and a STAFF login "Coach" when it is
 * linked to a staff record (`staffLinked`), else "Front desk". SUPER_ADMIN/USER/GUEST unchanged.
 */
export function roleBadge(
  role: UserRole,
  mode: 'light' | 'dark' = 'light',
  staffLinked = false,
): BadgeStyle {
  const badge = roleBadges(mode)[role];
  if (badge && role === 'STAFF' && staffLinked) return { ...badge, label: 'Coach' };
  return badge ?? { ...buildSemanticMaps(mode).status.paused, label: role };
}
